import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  injectBxIcons,
  barChart05Icon,
  calendarDateIcon,
  clockCheckIcon,
  clockFastForwardIcon,
  clockIcon,
  cpuChip01Icon,
  flashIcon,
} from '@beyonnex/bx-angular-ui';
import { formatDateTime } from '../../../models/core.models';
import { CommonTranslateService } from '../../../services/common-translate/common-translate.service';
import { CurrentStatePanelComponent } from '../current-state-panel/current-state-panel.component';
import { CurrentStateDataPoint } from '../current-state-panel/current-state-panel.models';
import { DeviceDetailsStore } from '../device-details.store';

@Component({
  selector: 'bxi-basic-mbus-gateway-current-state',
  imports: [CurrentStatePanelComponent],
  templateUrl: './basic-mbus-gateway-current-state.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasicMbusGatewayCurrentStateComponent {
  private readonly store = inject(DeviceDetailsStore);
  protected readonly commonTranslate = inject(CommonTranslateService);

  protected readonly gateway = toSignal(this.store.basicMbusGateway$);

  // A missing feature blanks its own value and never drops its row, so the panel keeps the same shape on
  // every gateway — unlike ts-current-state, which filters absent probes out.
  protected readonly data = computed<CurrentStateDataPoint[]>(() => {
    const gateway = this.gateway();
    const cellular = gateway?.features?.cellularMeasurement?.properties;
    const mbusInfo = gateway?.features?.gatewayMBusInfo?.properties?.status;
    const readoutSchedule = gateway?.features?.meteringSchedule?.properties?.configuration?.readoutScheduleConfiguration;

    return [
      {
        label: this.commonTranslate.translateThingProperty('features.cellularMeasurement.properties.measurementResult'),
        value: this.commonTranslate.translateConnectionQuality(cellular?.measurementResult),
        icon: 'bar-chart-05',
        iconStyle: 'text-teal-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.cellularMeasurement.properties.measurementSamples'),
        value: formatDateTime(gateway?.getLatestCellularSample()?.timestamp),
        // Three clock-family icons share this panel, so each keeps a colour of its own.
        icon: 'clock-check',
        iconStyle: 'text-utility-purple-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.gatewayMBusInfo.properties.status.battery.stateOfCharge'),
        // Raw, not formatPercent: the schema says percent but 255 was observed on a real gateway.
        value: mbusInfo?.battery?.stateOfCharge,
        icon: 'flash',
        iconStyle: 'text-fg-warning-primary',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.gatewayMBusInfo.properties.status.firmware'),
        value: mbusInfo?.firmware,
        icon: 'cpu-chip-01',
        iconStyle: 'text-utility-orange-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.connectionStatus.properties.status.nextConnection'),
        value: formatDateTime(gateway?.features?.connectionStatus?.properties?.status?.nextConnection),
        icon: 'clock-fast-forward',
        iconStyle: 'text-utility-indigo-500',
      },
      {
        label: this.commonTranslate.translateThingProperty(
          'features.meteringSchedule.properties.configuration.readoutScheduleConfiguration.daysOfMonth',
        ),
        value: (readoutSchedule?.daysOfMonth ?? []).join(', '),
        icon: 'calendar-date',
        iconStyle: 'text-utility-fuchsia-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.comController.properties.lastMbusTransmission'),
        value: formatDateTime(gateway?.features?.comController?.properties?.lastMbusTransmission),
        icon: 'clock',
        iconStyle: 'text-utility-blue-light-600',
      },
    ];
  });

  constructor() {
    injectBxIcons([barChart05Icon, calendarDateIcon, clockCheckIcon, clockFastForwardIcon, clockIcon, cpuChip01Icon, flashIcon]);
  }
}
